import { ContextSelectedOptions } from "@appretio/context-selector";
import { Button } from "@appretio/ui-kit";
import React, { useMemo } from "react";
import { CategoryChart } from "../../../components/charts/category/CategoryChart";
import { TemporalContext } from "../../../components/temporalContextSelector/data/temporalContextOptions";
import { useCategoryAggregatesData } from "../hooks/useCategoryAggregatesData";
import { ReviewDataResponse } from "../hooks/useFetchReviewData";

/**
 * Props for the CategoryTab component
 */
interface CategoryTabProps {
  /** Applied context and temporal filters */
  appliedFilters: {
    contextOptions: ContextSelectedOptions;
    temporalOptions: TemporalContext;
  };
  /** Whether user has applied filters to enable data fetching */
  hasUserApplied: boolean;
  /** Selected rows from the review data grid for filtering chart data */
  selectedRowData: ReviewDataResponse;
  /**
   * Local category selection for chart drill-down
   */
  chartSelectedCategory?: string;
  /**
   * Callback when a category is clicked for drill-down
   */
  onCategoryClick?: (category: string) => void;
  /**
   * Callback to clear chart category selection
   */
  onClearCategorySelection?: () => void;
}

/**
 * CategoryTab component displays category and subcategory aggregates in a chart.
 *
 * Features:
 * - Shows category-level view by default with sales, purchases, and sellthrough percentages
 * - Supports drill-down to subcategory view when a category is clicked
 * - Filters chart data based on selected grid rows
 * - Automatically switches between category and subcategory levels
 * - Provides back button to return to category view from drill-down
 * - Forces chart remount when switching levels to reset zoom/pan state
 */
export const CategoryTab = React.memo(function CategoryTab({
  appliedFilters,
  hasUserApplied,
  selectedRowData,
  onCategoryClick,
  chartSelectedCategory,
  onClearCategorySelection
}: CategoryTabProps) {
  // Get selected category from context options OR local chart selection
  const selectedCategoriesFromContext =
    appliedFilters.contextOptions.product_category || [];
  const contextSelectedCategory =
    selectedCategoriesFromContext.length === 1
      ? selectedCategoriesFromContext[0]
      : "";

  // Use chart selection if available, otherwise use context selection
  const selectedCategory = chartSelectedCategory || contextSelectedCategory;

  // Dynamic level based on whether a single category is selected
  const level = selectedCategory ? "subcategory" : "category";

  const CHART_TITLE = "Sales, Purchase & Sellthrough";

  const { data: categoryAggregates, isFetching } = useCategoryAggregatesData({
    contextOptions: appliedFilters.contextOptions,
    temporalOptions: appliedFilters.temporalOptions,
    level,
    selectedCategory,
    enabled: hasUserApplied // only fetch when user applies filters
  });

  // Optional: filter category data to only include selected rows (if applicable)
  const filteredCategoryData = useMemo(() => {
    if (!categoryAggregates || !Array.isArray(categoryAggregates)) return [];

    // If no selected rows, show all categories/subcategories
    if (!selectedRowData || selectedRowData.length === 0)
      return categoryAggregates;

    // For category level, filter by product_category
    if (level === "category") {
      const selectedCategories = new Set(
        selectedRowData.map((row) => row.product_category)
      );

      return categoryAggregates.filter((category) =>
        selectedCategories.has(category.product_category)
      );
    }

    // For subcategory level, filter by style_subcategory
    else {
      const selectedSubcategories = new Set(
        selectedRowData.map((row) => row.style_subcategory)
      );

      return categoryAggregates.filter((subcat) =>
        selectedSubcategories.has(subcat.style_subcategory)
      );
    }
  }, [selectedRowData, categoryAggregates, level]);

  // Optional: early returns for UX clarity
  if (isFetching) {
    return (
      <CategoryChart
        inSeasonReviewData={[]}
        isLoading={true}
        title={CHART_TITLE}
      />
    );
  }

  return (
    <div>
      {/* Show back button when viewing subcategories from chart drill-down */}
      {chartSelectedCategory && onClearCategorySelection && (
        <div style={{ marginBottom: "16px" }}>
          <Button
            variant="tertiary"
            size="sm"
            onClick={onClearCategorySelection}
          >
            ← Back to Categories
          </Button>
        </div>
      )}
      <CategoryChart
        key={`${level}-${selectedCategory || "all"}`}
        inSeasonReviewData={filteredCategoryData}
        isLoading={isFetching}
        title={CHART_TITLE}
        subtitle={
          selectedCategory ? `Category: ${selectedCategory}` : undefined
        }
        onCategoryClick={level === "category" ? onCategoryClick : undefined}
      />
    </div>
  );
});
